import { useState, useCallback } from "react";
import { 
  ConfigSidebarState,
  InterjectionGroup,
  InterjectionContent,
  InterjectionType
} from "../utils/types";

// Config Sidebar Hook (renamed from useInterjectionSidebar)
export const useConfigSidebar = () => {
  const [configState, setConfigState] = useState<ConfigSidebarState>({
    isVisible: false,
    groups: [],
    editingGroup: null,
    isAddingNewGroup: false,
    editingContent: null,
    isAddingContent: null,
  });

  const updateConfigState = useCallback((updates: Partial<ConfigSidebarState>) => {
    setConfigState(prev => ({ ...prev, ...updates }));
  }, []);

  const addGroup = useCallback((name: string) => {
    const newGroup: InterjectionGroup = {
      id: `group-${Date.now()}`,
      type: "other",
      groupName: name,
      interjection_rate: 0.1,
      within_script: false,
      repeat_script: false,
      content: []
    };
    
    setConfigState(prev => ({
      ...prev,
      groups: [...prev.groups, newGroup],
      isAddingNewGroup: false
    }));
  }, []);

  const updateGroup = useCallback((groupId: string, name: string) => {
    setConfigState(prev => ({
      ...prev,
      groups: prev.groups.map(group => 
        group.id === groupId ? { ...group, groupName: name } : group
      ),
      editingGroup: null
    }));
  }, []);

  const deleteGroup = useCallback((groupId: string) => {
    setConfigState(prev => ({
      ...prev,
      groups: prev.groups.filter(group => group.id !== groupId)
    }));
  }, []);

  const addContent = useCallback((groupId: string, content: Omit<InterjectionContent, "id">) => {
    const newContent: InterjectionContent = {
      ...content,
      id: `content-${Date.now()}`
    };
    
    setConfigState(prev => ({
      ...prev,
      groups: prev.groups.map(group => 
        group.id === groupId 
          ? { ...group, content: [...group.content, newContent] }
          : group
      )
    }));
  }, []);

  const updateContent = useCallback((groupId: string, contentId: string, content: Omit<InterjectionContent, "id">) => {
    setConfigState(prev => ({
      ...prev,
      groups: prev.groups.map(group => 
        group.id === groupId 
          ? {
              ...group,
              content: group.content.map((c: InterjectionContent) => 
                c.id === contentId ? { ...content, id: contentId } : c
              )
            }
          : group
      ),
      editingContent: null
    }));
  }, []);

  const deleteContent = useCallback((groupId: string, contentId: string) => {
    setConfigState(prev => ({
      ...prev,
      groups: prev.groups.map(group => 
        group.id === groupId 
          ? { ...group, content: group.content.filter((c: InterjectionContent) => c.id !== contentId) }
          : group
      )
    }));
  }, []);

  const moveContent = useCallback((contentId: string, fromGroupId: string, toGroupId: string) => {
    setConfigState(prev => {
      const fromGroup = prev.groups.find(g => g.id === fromGroupId);
      const content = fromGroup?.content.find((c: InterjectionContent) => c.id === contentId);
      
      if (!content) return prev;
      
      return {
        ...prev,
        groups: prev.groups.map(group => {
          if (group.id === fromGroupId) {
            return { ...group, content: group.content.filter((c: InterjectionContent) => c.id !== contentId) };
          }
          if (group.id === toGroupId) {
            return { ...group, content: [...group.content, content] };
          }
          return group;
        })
      };
    });
  }, []);

  // Additional functions that the main component expects
  const startAddingNewGroup = useCallback(() => {
    updateConfigState({ isAddingNewGroup: true, editingGroup: null });
  }, [updateConfigState]);

  const startEditingGroup = useCallback((group: InterjectionGroup) => {
    updateConfigState({ editingGroup: group, isAddingNewGroup: false });
  }, [updateConfigState]);

  const cancelEditing = useCallback(() => {
    updateConfigState({ editingGroup: null, isAddingNewGroup: false, editingContent: null, isAddingContent: null });
  }, [updateConfigState]);

  const saveInterjectionGroup = useCallback((groupData: {
    type: InterjectionType;
    customTypeName?: string;
    groupName?: string;
    interjection_rate: number;
    within_script: boolean;
    repeat_script: boolean;
  }) => {
    // Handle both new groups and updates
    if (configState.editingGroup) {
      // Update existing group
      setConfigState(prev => ({
        ...prev,
        groups: prev.groups.map(group => 
          group.id === configState.editingGroup!.id 
            ? { ...group, ...groupData }
            : group
        ),
        editingGroup: null
      }));
    } else {
      // Add new group
      const newGroup: InterjectionGroup = {
        id: `group-${Date.now()}`,
        ...groupData,
        content: []
      };
      setConfigState(prev => ({
        ...prev,
        groups: [...prev.groups, newGroup],
        isAddingNewGroup: false
      }));
    }
  }, [configState.editingGroup]);

  const deleteInterjectionGroup = useCallback((groupId: string) => {
    deleteGroup(groupId);
  }, [deleteGroup]);

  const startAddingContent = useCallback((groupId: string) => {
    setConfigState(prev => ({ ...prev, isAddingContent: groupId }));
  }, []);

  const startEditingContent = useCallback((groupId: string, contentId: string) => {
    setConfigState(prev => ({ ...prev, editingContent: { groupId, contentId } }));
  }, []);

  const saveContent = useCallback((interjection_text: string, interjection_response: string) => {
    const content: Omit<InterjectionContent, "id"> = {
      interjection_text,
      interjection_response
    };

    if (configState.editingContent) {
      // Update existing content
      const { groupId, contentId } = configState.editingContent;
      if (contentId) {
        updateContent(groupId, contentId, content);
      } else {
        addContent(groupId, content);
      }
    } else if (configState.isAddingContent) {
      // Add new content to the specified group
      addContent(configState.isAddingContent, content);
    }
    
    // Clear editing states
    setConfigState(prev => ({ 
      ...prev, 
      editingContent: null, 
      isAddingContent: null 
    }));
  }, [configState.editingContent, configState.isAddingContent, updateContent, addContent]);

  // Direct save API to add content for a specific group (used by UI to avoid timing/race issues)
  const saveContentForGroup = useCallback((groupId: string, interjection_text: string, interjection_response: string, contentId?: string) => {
    const content: Omit<InterjectionContent, "id"> = {
      interjection_text,
      interjection_response
    };
    
    if (contentId) {
      // Update existing content
      updateContent(groupId, contentId, content);
    } else {
      // Add new content
      addContent(groupId, content);
    }
    
    // Clear transient add/edit state if it was set
    setConfigState(prev => ({ ...prev, editingContent: null, isAddingContent: null }));
  }, [addContent, updateContent]);

  // Add a specific function to update existing content by IDs  
  const updateContentForGroup = useCallback((groupId: string, contentId: string, interjection_text: string, interjection_response: string) => {
    const content: Omit<InterjectionContent, "id"> = {
      interjection_text,
      interjection_response
    };
    updateContent(groupId, contentId, content);
    // Clear transient edit state
    setConfigState(prev => ({ ...prev, editingContent: null }));
  }, [updateContent]);

  const clearGroups = useCallback(() => {
    setConfigState(prev => ({ ...prev, groups: [] }));
  }, []);

  const setGroups = useCallback((groups: InterjectionGroup[]) => {
    setConfigState(prev => ({ ...prev, groups }));
  }, []);

  // Legacy compatibility - map old interjection names to new config names
  const result = {
    configState,
    // New group-based functions
    startAddingNewGroup,
    startEditingGroup,
    cancelEditing,
    saveInterjectionGroup,
    deleteInterjectionGroup: deleteGroup,
    startAddingContent,
    startEditingContent,
    saveContent,
    saveContentForGroup,
    updateContentForGroup,
    deleteContent,
    clearGroups,
    setGroups,
  };

  return {
    interjectionState: result.configState, // Map to old name for compatibility
    ...result,
  };
};